<template>
  <div class="min-h-screen bg-gray-50 py-8 px-4">
    <div class="max-w-7xl mx-auto mt-20">
      <!-- Header -->
     <div class="bg-gray-50 py-6 md:py-8 px-4 md:px-6">
    <div class="max-w-7xl mx-auto">
      <!-- Badge -->
      <div class="flex items-center gap-2 mb-3 md:mb-4">
        <div class="w-2 h-2 bg-[#07A766] rounded-full"></div>
        <span class="text-[#07A766] text-xs md:text-sm font-medium uppercase tracking-wide">
          Plateforme de Signalisation
        </span>
      </div>

      <!-- Titre principal -->
      <h1 class="text-2xl md:text-3xl lg:text-4xl font-bold text-[#0d4a6f] mb-2 md:mb-3">
        Signaler une irrégularité
      </h1>

      <!-- Description -->
      <p class="text-sm md:text-base text-gray-700 leading-relaxed max-w-3xl">
        Participez à la transparence et à la bonne gouvernance. Soumettez <br/>votre déclaration en toute confidentialité et sécurité.
      </p>
    </div>
  </div>

      <!-- Formulaire de test -->
      <div v-if="conditionsAccepted" class="bg-white rounded-lg shadow-lg p-6">
        <FormEngine
          :form="testForm"
          formId="test-form-engine"
          :currentLang="currentLang"
          :previewBeforeSubmission="true"
          :onSubmit="handleSubmit"
          :submitMethod="handleSubmitAsync"
        />
      </div>

      <!-- Message d'attente si les conditions ne sont pas acceptées -->
      <div v-else class="bg-white rounded-lg shadow-lg p-6 text-center">
        <p class="text-gray-600">Veuillez accepter les conditions pour continuer.</p>
      </div>
    </div>

    <!-- Modal d'avertissement - doit être accepté avant de commencer le formulaire -->
    <AvertissementComponents
      :show="!conditionsAccepted"
      @accept="handleConditionsAccepted"
      @reject="handleConditionsRejected"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import FormEngine from '../components/atoms/form-engine/components/FormEngine.vue';
import type { FormStructure } from '../components/atoms/form-engine/types/formTypeStructure';
import { useFormStore } from '../components/atoms/form-engine/store/useFormStore';
import { showToast } from '../components/atoms/form-engine/utils/toastHelpers';
import AvertissementComponents from '../components/AvertissementComponents.vue';
import { BACKEND_CORE_URL, BUCKET_SERVER_DNS, BUCKET_SECRET_KEY } from '@/config/api';

const currentLang = ref('fr');
const router = useRouter();
const formStore = useFormStore();
const conditionsAccepted = ref(false);

// Gérer l'acceptation des conditions
const handleConditionsAccepted = () => {
  conditionsAccepted.value = true;
  // Réactiver le scroll du body quand les conditions sont acceptées
  document.body.style.overflow = 'auto';
  console.log('✅ Conditions acceptées, affichage du formulaire');
};

// Gérer le rejet des conditions (rediriger vers l'accueil)
const handleConditionsRejected = () => {
  console.log('❌ Conditions refusées, redirection vers l\'accueil');
  // Réactiver le scroll avant la redirection
  document.body.style.overflow = 'auto';
  router.push({ name: 'home' });
};

// Surveiller les changements de conditionsAccepted pour réactiver le scroll
watch(conditionsAccepted, (newValue) => {
  if (newValue) {
    document.body.style.overflow = 'auto';
  }
});

// S'assurer que le scroll est réactivé au démontage du composant
onUnmounted(() => {
  document.body.style.overflow = 'auto';
});

// Utiliser la configuration centralisée avec support DEV/PROD
// Les URLs sont automatiquement sélectionnées selon VITE_ENV (dev ou prod)
const API_BASE_URL = BACKEND_CORE_URL

// Clé pour récupérer le token OTP depuis le store
// Format: ${serverDns}_${fieldName}
const OTP_TOKEN_STORAGE_KEY = `${API_BASE_URL}_code_otp`;

// Computed pour récupérer le token OTP stocké
const storedOTPToken = computed(() => {
  const token = formStore.getOTPToken(OTP_TOKEN_STORAGE_KEY);
  console.log('🔍 [Token Check] Clé utilisée:', OTP_TOKEN_STORAGE_KEY, 'Token trouvé:', token ? 'Oui' : 'Non', token);
  return token;
});

// Computed pour obtenir la valeur du token (pour utilisation dans les headers)
const tokenValue = computed(() => {
  const token = storedOTPToken.value;
  if (!token) return null;
  
  // Chercher la valeur du token dans différentes clés possibles
  const possibleKeys = ['access_token', 'accessToken', 'token', 'authToken', 'auth_token'];
  for (const key of possibleKeys) {
    if (token[key] && typeof token[key] === 'string') {
      return token[key];
    }
  }
  
  // Si aucune clé standard, chercher la première clé contenant "token"
  for (const [key, value] of Object.entries(token)) {
    if (key.toLowerCase().includes('token') && typeof value === 'string' && value.length > 10) {
      return value;
    }
  }
  
  return null;
});

// Computed pour vérifier si le token est valide
const isTokenValid = computed(() => {
  const token = storedOTPToken.value;
  if (!token) return false;
  
  // Vérifier l'expiration si expires_at est défini
  if (token.expires_at) {
    return token.expires_at > Date.now();
  }
  
  return true;
});

// Formulaire de test pour générer le JSON de signalement
const testForm: FormStructure = {
  name: 'Plateforme de Signalisation',
  provider: 'Cour des Comptes',
  description: 'Signaler une irrégularité. Participez à la transparence et à la bonne gouvernance. Soumettez votre déclaration en toute confidentialité et sécurité.',
  status: 'ACTIVE',
  isDeclaration: 0,
  isTest: 1,
  layoutOptions: {
    paginationMode: 'bySection',
    progressBarType: {
      type: 'section_bubble',
      visible: true,
      colors: {
        background: '#e5e7eb',
        foreground: '#0A7B4D'
      }
    }
  },
  sections: [
    {
      sectionId: 'section-1',
      title: 'Initialisation',
      subTitle: 'Sélectionnez votre OSC et la catégorie de signalement',
      position: 1,
      formFields: [
        {
          formFieldId: 'field-osc',
          fieldName: 'osc_id',
          fieldType: 'dropdown',
          label: 'Quel OSC êtes-vous ?',
          response: { responseValue: '' },
          // Les options seront chargées dynamiquement depuis l'API /oscs
          selectOptions: [],
          validations: [
            {
              validationType: 'required',
              errMsg: 'La sélection de votre OSC est obligatoire'
            }
          ],
          position: 1
        },
        {
          formFieldId: 'field-1',
          fieldName: 'type_signalement',
          fieldType: 'dropdown',
          label: 'Catégories',
          response: { responseValue: '' },
          // Les options seront chargées dynamiquement depuis l'API /categories
          selectOptions: [],
          validations: [
            {
              validationType: 'required',
              errMsg: 'La catégorie de signalement est obligatoire'
            }
          ],
          position: 2
        },
        {
          formFieldId: 'field-objet',
          fieldName: 'objet',
          fieldType: 'text',
          label: 'Objet / Titre du signalement',
          response: { responseValue: '' },
          validations: [
            {
              validationType: 'required',
              errMsg: "L'objet du signalement est obligatoire"
            },
            {
              validationType: 'maxLength',
              value: 255,
              errMsg: "L'objet ne doit pas dépasser 255 caractères"
            }
          ],
          placeholder: 'Ex : Suspicion de détournement de fonds publics',
          position: 3
        }
      ]
    },
    {
      sectionId: 'section-2',
      title: 'Vérification OTP',
      subTitle: 'Entrez le code de vérification reçu',
      position: 2,
      formFields: [
        {
          formFieldId: 'field-otp',
          fieldName: 'code_otp',
          fieldType: 'OTP',
          label: 'Code de vérification',
          response: { responseValue: '' },
          otpFormFieldExecOptions: {
            serverDns: API_BASE_URL,
            postApiEndPoint: '/osc/auth/verify-otp',
            otpLength: 6,
            timeoutMs: 15000,
            payload: {
              // osc_id sera ajouté automatiquement depuis formValues
            },
            extraHeaders: {
              'Content-Type': 'application/json'
            },
            autoValidate: false,
            enableResend: true,
            resendCooldownSeconds: 60,
            linkedEmailFieldName: 'osc_id', // Lier au champ OSC pour déclencher l'envoi automatique
            // Configuration pour l'envoi initial de l'OTP (quand l'OSC est sélectionnée)
            sendOTPApiConfig: {
              serverDns: API_BASE_URL,
              postApiEndPoint: '/osc/auth/generate-otp',
              payload: {
                // osc_id sera ajouté automatiquement depuis formValues
              },
              extraHeaders: {
                'Content-Type': 'application/json'
              }
            }
          },
          validations: [
            {
              validationType: 'required',
              errMsg: 'Le code OTP est obligatoire'
            }
          ],
          position: 1
        }
      ]
    },
    {
      sectionId: 'section-3',
      title: "Description de l'irrégularité",
      subTitle: "Veuillez décrire l'irrégularité. Soyez précis et factuel.",
      position: 3,
      formFields: [
        {
          formFieldId: 'field-structure',
          fieldName: 'organisme_nom',
          fieldType: 'text',
          label: 'Nom de l\'organisme mis en cause',
          response: { responseValue: '' },
          validations: [
            {
              validationType: 'required',
              errMsg: 'Le nom de l\'organisme est obligatoire'
            }
          ],
          placeholder: 'Ex: Ministère des Finances',
          position: 1
        },
        {
          formFieldId: 'field-adresse',
          fieldName: 'organisme_adresse',
          fieldType: 'text',
          label: 'Adresse de l\'organisme',
          response: { responseValue: '' },
          validations: [
            {
              validationType: 'required',
              errMsg: "L'adresse de l'organisme est obligatoire"
            }
          ],
          placeholder: 'Ex: Cotonou, Bénin',
          position: 2
        },
        {
          formFieldId: 'field-organisme-type',
          fieldName: 'organisme_type',
          fieldType: 'dropdown',
          label: 'Type d\'organisme',
          response: { responseValue: '' },
          selectOptions: [
            { value: 'Institution publique', label: 'Institution publique' },
            { value: 'Entreprise privée', label: 'Entreprise privée' },
            { value: 'Organisation internationale', label: 'Organisation internationale' },
            { value: 'Autre', label: 'Autre' }
          ],
          validations: [
            {
              validationType: 'required',
              errMsg: 'Le type d\'organisme est obligatoire'
            }
          ],
          position: 3
        },
        {
          formFieldId: 'field-connaissance',
          fieldName: 'comment_connaissance',
          fieldType: 'text',
          label: 'Comment avez-vous eu connaissance de cette situation ?',
          response: { responseValue: '' },
          validations: [
            {
              validationType: 'required',
              errMsg: 'Ce champ est obligatoire'
            }
          ],
          placeholder: 'Ex: Par un témoin, lors d\'une réunion, etc.',
          position: 4
        },
        {
          formFieldId: 'field-date-faits',
          fieldName: 'date_faits',
          fieldType: 'date',
          label: 'Date des faits',
          response: { responseValue: '' },
          validations: [
            {
              validationType: 'required',
              errMsg: 'La date des faits est obligatoire'
            }
          ],
          position: 5
        },
        {
          formFieldId: 'field-signalement-ailleurs-oui-non',
          fieldName: 'signalement_ailleurs_oui_non',
          fieldType: 'radio',
          label: 'Avez-vous signalé les faits à un autre organisme ?',
          response: { responseValue: '' },
          selectOptions: [
            { value: 'Oui', label: 'Oui' },
            { value: 'Non', label: 'Non' }
          ],
          validations: [
            {
              validationType: 'required',
              errMsg: 'Veuillez indiquer si vous avez signalé les faits à un autre organisme.'
            }
          ],
          position: 6
        },
        {
          formFieldId: 'field-signale-autre-precision',
          fieldName: 'signalement_autre_organisme',
          fieldType: 'textarea',
          label: 'Si oui, précisez.',
          response: { responseValue: '' },
          validations: [
            {
              validationType: 'required',
              errMsg: "Veuillez préciser à quel(s) organisme(s) vous avez signalé les faits."
            }
          ],
          placeholder: "Ex : J'ai signalé à la Police nationale...",
          position: 7,
          conditionalDisplay: {
            rules: [
              {
                fieldName: 'signalement_ailleurs_oui_non',
                operator: '==',
                value: 'Oui'
              }
            ],
            logic: 'AND'
          }
        },
        {
          formFieldId: 'field-description-faits',
          fieldName: 'description_faits',
          fieldType: 'textarea',
          label: 'Décrivez précisément les faits :',
          response: { responseValue: '' },
          validations: [
            {
              validationType: 'required',
              errMsg: 'La description des faits est obligatoire'
            },
            {
              validationType: 'minLength',
              value: 50,
              errMsg: 'La description doit contenir au moins 50 caractères'
            }
          ],
          placeholder: 'Décrivez précisément les faits en détail...',
          position: 8,
          width: 'full' // Largeur complète sur tous les écrans
        },
        {
          formFieldId: 'field-fichier',
          fieldName: 'documents',
          fieldType: 'file',
          label: 'Déposez un fichier ici ou cliquez sur le bouton ci-dessous pour charger le fichier.',
          response: { responseValue: [] },
          fileToBucketManage: {
            uploadOption: {
              serverDns: BUCKET_SERVER_DNS,
              postApiEndPoint: '/api/files/upload-file',
              payload: {
                folder_name: 'signalements-documents'
              },
              extraHeaders: {
                'x-secret-key': BUCKET_SECRET_KEY
              }
            },
            deleteOption: {
              serverDns: BUCKET_SERVER_DNS,
              deleteApiEndPoint: '/api/files/delete-file-by-url',
              payload: {},
              extraHeaders: {
                'x-secret-key': BUCKET_SECRET_KEY
              }
            }
          },
          position: 9,
          width: 'full' // Largeur complète sur tous les écrans
        },
        {
          formFieldId: 'field-transmission',
          fieldName: 'confirmation_transmission',
          fieldType: 'checkbox',
          label: 'Transmission',
          response: { responseValue: [] }, // Checkbox attend un tableau (string[]), pas un booléen
          selectOptions: [
            {
              value: 'confirmed',
              label: 'Je confirme vous transmettre mon signalement de manière désintéressée et de bonne foi'
            }
          ],
          validations: [
            {
              validationType: 'required',
              errMsg: 'Vous devez confirmer la transmission de votre signalement'
            }
          ],
          position: 10,
          width: 'full' // Largeur complète sur tous les écrans
        }
      ]
    }
  ]
};

const loadOSCs = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/oscs`, {
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      console.error('❌ Échec du chargement des OSCs', await response.text());
      return;
    }

    const json = await response.json();
    // L'API renvoie un tableau d'OSCs
    const items = Array.isArray(json) ? json : Array.isArray(json.data) ? json.data : [];
    if (!Array.isArray(items)) {
      console.warn('⚠️ Réponse inattendue pour /oscs :', json);
      return;
    }

    const options = items.map((osc: any) => ({
      value: osc.id ?? osc.identifiant,
      label: osc.nom ?? String(osc.id ?? osc.identifiant)
    }));

    const oscField = testForm.sections[0]?.formFields.find(
      (f) => f.fieldName === 'osc_id'
    );
    if (oscField) {
      oscField.selectOptions = options;
      console.log('✅ OSCs chargées:', options.length);
    }
  } catch (error) {
    console.error('❌ Erreur lors du chargement des OSCs:', error);
  }
};

const loadCategories = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/categories`, {
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      console.error('❌ Échec du chargement des catégories', await response.text());
      return;
    }

    const json = await response.json();
    // L'API renvoie un objet { data: [...] }
    const items = Array.isArray(json) ? json : Array.isArray(json.data) ? json.data : [];
    if (!Array.isArray(items)) {
      console.warn('⚠️ Réponse inattendue pour /categories :', json);
      return;
    }

    const options = items.map((cat: any) => ({
      value: cat.nom ?? cat.id,
      label: cat.nom ?? String(cat.id)
    }));

    const typeField = testForm.sections[0]?.formFields.find(
      (f) => f.fieldName === 'type_signalement'
    );
    if (typeField) {
      typeField.selectOptions = options;
    }
  } catch (error) {
    console.error('❌ Erreur lors du chargement des catégories:', error);
  }
};

onMounted(() => {
  // Charger les OSCs et les catégories
  loadOSCs();
  loadCategories();
});

// Fonction pour transformer les données plates en structure JSON imbriquée selon le schéma
const transformToNestedStructure = (flatData: Record<string, any>) => {
  // Traiter les documents (peut être un array ou une string)
  let documents: string[] = [];
  if (Array.isArray(flatData.documents)) {
    documents = flatData.documents.filter((doc: any) => 
      typeof doc === 'string' && doc.trim() !== ''
    );
  } else if (typeof flatData.documents === 'string' && flatData.documents.trim() !== '') {
    documents = [flatData.documents];
  }

  // Formater la date_faits si elle existe (s'assurer du format YYYY-MM-DD)
  let dateFaits = '';
  if (flatData.date_faits) {
    try {
      const date = new Date(flatData.date_faits);
      if (!isNaN(date.getTime())) {
        dateFaits = date.toISOString().split('T')[0];
      } else {
        dateFaits = flatData.date_faits; // Garder la valeur telle quelle si ce n'est pas une date valide
      }
    } catch {
      dateFaits = flatData.date_faits;
    }
  }

  // Construire la structure JSON selon le schéma attendu
  const nestedData: Record<string, any> = {
    objet: (flatData.objet ?? '').toString().trim().slice(0, 255),
    type_signalement: flatData.type_signalement || '',
    soumission: {
      date: new Date().toISOString().split('T')[0], // Date du jour au format YYYY-MM-DD
      canal: 'Portail en ligne',
      documents: documents
    },
    description_faits: {
      description: flatData.description_faits || '',
      lieu: flatData.organisme_adresse || '', // Utiliser organisme_adresse comme lieu
      date_faits: dateFaits,
      comment_connaissance: flatData.comment_connaissance || '',
      signalement_autre_organisme:
        flatData.signalement_ailleurs_oui_non === 'Non'
          ? "Non, aucun autre organisme n'a encore été informé."
          : (flatData.signalement_autre_organisme || '').trim()
    },
    organisme_mis_en_cause: {
      nom: flatData.organisme_nom || '',
      type: flatData.organisme_type || '',
      adresse: flatData.organisme_adresse || ''
    }
  };

  return nestedData;
};

// Gestionnaire de soumission synchrone (non utilisé, mais requis par FormEngine)
const handleSubmit = (data: Record<string, any>) => {
  console.log('📤 Données soumises (synchrone):', data);
  // La soumission asynchrone est gérée par handleSubmitAsync
};

// Gestionnaire de soumission asynchrone
const handleSubmitAsync = async (data: Record<string, any>): Promise<any> => {
  console.log('📤 Données soumises (asynchrone):', data);
  
  // Transformer les données plates en structure imbriquée
  const payload = transformToNestedStructure(data);
  console.log('📦 Payload transformé:', payload);
  
  // Vérifier et utiliser le token OTP
  const token = storedOTPToken.value;
  const tokenVal = tokenValue.value;
  
  if (!token || !tokenVal || !isTokenValid.value) {
    const errorMsg = 'Token OTP manquant ou expiré. Veuillez refaire la vérification OTP.';
    console.error('❌ [Submit]', errorMsg);
    showToast(errorMsg, 'error');
    throw new Error(errorMsg);
  }
  
  try {
    console.log('🔐 [Submit] Envoi du signalement avec token OTP:', {
      tokenType: token.token_type || 'bearer',
      tokenValue: tokenVal.substring(0, 20) + '...',
      expiresAt: token.expires_at ? new Date(token.expires_at).toISOString() : 'N/A'
    });
    
    // Envoyer la requête POST à l'API
    const response = await fetch(`${API_BASE_URL}/osc/signalements`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `${token.token_type || 'bearer'} ${tokenVal}`,
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    // Gérer les erreurs HTTP
    if (!response.ok) {
      let errorMessage = `Erreur ${response.status}: ${response.statusText}`;
      
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // Si la réponse n'est pas du JSON, utiliser le texte
        const text = await response.text();
        errorMessage = text || errorMessage;
      }
      
      console.error('❌ [Submit] Erreur API:', errorMessage);
      
      // Tronquer le message pour le toast (max 100 caractères)
      const truncatedMsg = errorMessage.length > 100 ? errorMessage.substring(0, 100) + '...' : errorMessage;
      showToast(truncatedMsg, 'error');
      
      throw new Error(errorMessage);
    }
    
    // Succès : récupérer les données de réponse
    const result = await response.json();
    console.log('✅ [Submit] Signalement créé avec succès:', result);
    console.log('🔍 [Submit] Clés disponibles dans result:', Object.keys(result));
    
    // Afficher un toast de succès
    showToast('Signalement créé avec succès !', 'success');
    
    // Rediriger vers la page de confirmation avec le code_suivi
    // Vérifier différentes variantes possibles du nom du champ
    const codeSuivi = result.code_suivi || result.codeSuivi || result.code_suivi || result.code;
    const idSignalement = result.id_signalement || result.idSignalement || result.id;
    
    if (codeSuivi) {
      // Utiliser router.push pour la redirection (SPA navigation)
      const queryParams: Record<string, string> = {
        code: String(codeSuivi)
      };
      if (idSignalement) {
        queryParams.id = String(idSignalement);
      }
      console.log('🔄 [Submit] Redirection vers /confirmation avec:', queryParams);
      
      // Petit délai pour laisser le toast s'afficher
      setTimeout(() => {
        router.push({
          name: 'confirmation',
          query: queryParams
        }).catch((error) => {
          console.error('❌ [Submit] Erreur lors de la redirection:', error);
          // Fallback : utiliser window.location si router.push échoue
          const confirmationUrl = `/confirmation?code=${encodeURIComponent(String(codeSuivi))}${idSignalement ? `&id=${encodeURIComponent(String(idSignalement))}` : ''}`;
          window.location.href = confirmationUrl;
        });
      }, 500);
    } else {
      // Fallback si pas de code_suivi : rediriger vers l'accueil
      console.warn('⚠️ [Submit] Pas de code_suivi dans la réponse. Résultat complet:', result);
      showToast('Signalement créé mais aucun code de suivi retourné', 'warning');
      setTimeout(() => {
        router.push({ name: 'home' });
      }, 2000);
    }
    
    return result;
  } catch (error) {
    // Gérer les erreurs réseau ou autres
    const errorMessage = error instanceof Error ? error.message : 'Erreur lors de l\'envoi du signalement';
    console.error('❌ [Submit] Erreur:', errorMessage);
    
    // Tronquer le message pour le toast
    const truncatedMsg = errorMessage.length > 100 ? errorMessage.substring(0, 100) + '...' : errorMessage;
    showToast(truncatedMsg, 'error');
    
    throw error;
  }
};

</script>

<style scoped>
/* Styles additionnels si nécessaire */
</style>

